import { useState, useCallback } from "react"
import type { AuditExecutionState, AuditProduct } from "../types"
import { useCompleteProductAuditMutation } from "src/graphql/quark/quark.type"
import { toast } from "react-toastify"

export const useAuditExecution = (auditId: string) => {
  const [state, setState] = useState<AuditExecutionState>({
    currentStep: "SELECT_PRODUCT",
    selectedProduct: null,
    isLoading: false,
    isSubmitted: false,
    error: null
  })

  const selectProduct = useCallback((product: AuditProduct) => {
    if (product.status === "COMPLETED") {
      // Show toast for completed products
      setState((prev) => ({ ...prev, error: "This product audit is already completed!" }))
      return
    }

    setState((prev) => ({
      ...prev,
      selectedProduct: product,
      currentStep: "SCAN_LOTS",
      error: null
    }))
  }, [])

  const [completeProductAudit, { loading: completeProductAuditLoading }] = useCompleteProductAuditMutation()

  const submitAudit = useCallback(async () => {
    try {
      if (!state?.selectedProduct?.id) {
        toast.error("No product selected")
        return
      }
      // Mock API call
      const resp = await completeProductAudit({
        variables: {
          auditId,
          productId: state.selectedProduct.id
        }
      })
      if (resp?.data?.completeProductAudit)
        setState((prev) => ({
          ...prev,
          currentStep: "CONFIRM",
          isLoading: false,
          isSubmitted: true
        }))
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: "Failed to submit audit",
        isLoading: false
      }))
    }
  }, [state])

  const setStep = useCallback((step: AuditExecutionState["currentStep"]) => {
    switch (step) {
      case "SELECT_PRODUCT":
        resetState()
        break
      case "SCAN_LOTS":
      case "SHOW_SUMMARY":
      case "CONFIRM":
        setState((prev) => ({ ...prev, currentStep: step }))
        break
    }
  }, [])

  const confirmAndContinue = useCallback(() => {
    setState((prev) => ({
      ...prev,
      currentStep: "SELECT_PRODUCT",
      selectedProduct: null,
      error: null
    }))
  }, [])

  const resetState = useCallback(() => {
    setState({
      currentStep: "SELECT_PRODUCT",
      selectedProduct: null,
      isLoading: false,
      isSubmitted: false,
      error: null
    })
  }, [])

  return {
    state: { ...state, isLoading: completeProductAuditLoading || state.isLoading },
    setStep,
    selectProduct,
    submitAudit,
    confirmAndContinue,
    resetState
  }
}
